import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

// Building blocks shared by the Insights tabs (Plans 050–055).

export function playerLabel(p: { name: string; displayName: string | null }): string {
  return p.displayName?.trim() || p.name;
}

export function fmtM(n: number | null | undefined, digits = 2): string {
  return n == null ? '—' : n.toFixed(digits);
}

export function fmtPct(share: number | null | undefined): string {
  return share == null ? '—' : `${Math.round(share * 100)} %`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function StatTile({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className={cn(
          'text-xl font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-300',
          tone === 'warn' && 'text-amber-300',
          tone === 'bad' && 'text-red-300',
        )}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

export function Panel({
  title,
  actions,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
      <div className="flex items-center justify-between gap-2 border-b border-slate-800 px-3 py-2">
        <h2 className="text-sm font-medium">{title}</h2>
        {actions}
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded border border-slate-800 bg-slate-950 p-0.5 text-xs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded px-2 py-0.5',
            value === o.value ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Loading() {
  return <div className="text-sm text-slate-400">Loading…</div>;
}

/** Sortable table header cell. */
export function Th({
  children,
  onClick,
  active,
  left,
}: {
  children: ReactNode;
  onClick?: () => void;
  active?: boolean;
  left?: boolean;
}) {
  return (
    <th
      onClick={onClick}
      className={cn(
        'px-2 py-1',
        onClick && 'cursor-pointer select-none',
        left ? 'text-left' : 'text-right',
        active && 'text-white',
      )}
    >
      {children}
    </th>
  );
}
